import { create } from 'zustand';
import apiClient from '../lib/api';

export interface PerformanceReview {
  id: string;
  employeeId: string;
  employeeName: string;
  employeeCode: string;
  reviewerId: string;
  reviewerName: string;
  periodStart: string | null;
  periodEnd: string | null;
  status: string;
  rating: number | null;
  strengths: string;
  weaknesses: string;
  goals: string;
  comments: string;
  createdAt: string;
}

export interface PerformanceStats {
  total: number;
  draft: number;
  completed: number;
  average_rating: number | null;
}

export interface ReviewInput {
  employeeId: string;
  reviewerId?: string | null;
  periodStart?: string | null;
  periodEnd?: string | null;
  rating?: number | null;
  strengths?: string;
  weaknesses?: string;
  goals?: string;
  comments?: string;
  status?: string;
}

interface PerformanceState {
  reviews: PerformanceReview[];
  stats: PerformanceStats | null;
  isLoading: boolean;
  error: string | null;

  fetchReviews: (employeeId?: string) => Promise<void>;
  fetchStats: () => Promise<void>;
  createReview: (data: ReviewInput) => Promise<void>;
  updateReview: (id: string, data: Partial<ReviewInput>) => Promise<void>;
  deleteReview: (id: string) => Promise<void>;
}

// The API returns raw snake_case columns (pr.*) plus joined employee names.
function normalizeReview(r: any): PerformanceReview {
  return {
    id: r.id,
    employeeId: r.employee_id,
    employeeName: [r.first_name, r.last_name].filter(Boolean).join(' ') || '—',
    employeeCode: r.employee_code || '',
    reviewerId: r.reviewer_id || '',
    reviewerName: [r.reviewer_first_name, r.reviewer_last_name].filter(Boolean).join(' '),
    periodStart: r.period_start,
    periodEnd: r.period_end,
    status: r.status || 'draft',
    rating: r.rating ?? null,
    strengths: r.strengths || '',
    weaknesses: r.weaknesses || '',
    goals: r.goals || '',
    comments: r.comments || '',
    createdAt: r.created_at,
  };
}

export const usePerformanceStore = create<PerformanceState>((set, get) => ({
  reviews: [],
  stats: null,
  isLoading: false,
  error: null,

  fetchReviews: async (employeeId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.get('/people/performance/reviews', { params: { employeeId } });
      const rows = response.data?.data ?? [];
      set({ reviews: rows.map(normalizeReview), isLoading: false });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to fetch performance reviews', isLoading: false });
    }
  },

  fetchStats: async () => {
    try {
      const response = await apiClient.get('/people/performance/stats');
      set({ stats: response.data?.data ?? response.data });
    } catch (error: any) {
      console.error('Failed to fetch performance stats', error);
    }
  },

  createReview: async (data) => {
    set({ error: null });
    try {
      await apiClient.post('/people/performance/reviews', data);
      await Promise.all([get().fetchReviews(), get().fetchStats()]);
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to create performance review' });
      throw error;
    }
  },

  updateReview: async (id, data) => {
    set({ error: null });
    try {
      await apiClient.patch(`/people/performance/reviews/${id}`, data);
      await Promise.all([get().fetchReviews(), get().fetchStats()]);
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to update performance review' });
      throw error;
    }
  },

  deleteReview: async (id) => {
    set({ error: null });
    try {
      await apiClient.delete(`/people/performance/reviews/${id}`);
      set((state) => ({ reviews: state.reviews.filter((r) => r.id !== id) }));
      await get().fetchStats();
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to delete performance review' });
      throw error;
    }
  },
}));
